import type {
  DiscoveryService,
  ProductService,
  SearchService,
  StoreService,
} from '@beautyburst/customer';
import type { LocationService } from '@beautyburst/customer';
import type {
  AccountService,
  AddressService,
  FavoritesService,
  ListsService,
  NotificationPreferencesService,
} from '@beautyburst/accounts';
import type { CartService } from '@beautyburst/cart';
import type { CheckoutService, OrdersService } from '@beautyburst/checkout';
import type { FulfilmentService } from '@beautyburst/fulfilment';
import type { PaymentService } from '@beautyburst/payments';
import type { ReturnsService } from '@beautyburst/refunds';
import type {
  CustomerLeadsService,
  MembershipInterestService,
  RetailerLeadsService,
} from '@beautyburst/leads';
import type { MembershipService } from '@beautyburst/membership';
import type { CustomerAnalyticsClient } from './analytics/client.ts';

/**
 * The data contract the customer screens are written against.
 *
 * Every response type is derived from the service that actually produces it
 * rather than restated by hand. That is deliberate: a hand-copied view model
 * drifts from the API the first time a field is renamed, and the screens would
 * keep compiling while rendering `undefined`. Deriving the types means a change
 * in a service breaks the build here instead.
 *
 * Screens receive this as a prop and never call `fetch` themselves, so every
 * screen can be tested without a network or a running database.
 */
type Result<T extends (...args: never[]) => unknown> = Awaited<ReturnType<T>>;

export interface CustomerDeliveryQuoteGroup {
  readonly cartSellerGroupId: string;
  readonly retailerId: string;
  readonly locationId: string;
  readonly sellerDisplayName: string;
  readonly fulfillmentMode: 'UNSELECTED' | 'PICKUP' | 'DELIVERY' | 'OVERNIGHT';
  readonly subtotalMinor: number;
  readonly normalBaseFeeMinor: number;
  readonly surchargeMinor: number;
  readonly benefitMinor: number;
  readonly deliveryFeeMinor: number;
  readonly totalDeliveryMinor: number;
  readonly freeDelivery: boolean;
  readonly benefitLabel: string | null;
}

/**
 * Server-authored delivery quote displayed before a customer places an order.
 * Amounts are integer minor units and are deliberately never calculated in the
 * browser.
 */
export interface CustomerDeliveryQuote {
  readonly quoteId: string | null;
  readonly cartId: string;
  readonly addressId: string | null;
  readonly currency: string;
  readonly subtotalMinor: number;
  readonly taxMinor: number;
  readonly discountMinor: number;
  readonly normalDeliveryBaseFeeMinor: number;
  readonly deliverySurchargeMinor: number;
  readonly deliveryBenefitMinor: number;
  readonly deliveryFeeMinor: number;
  readonly totalMinor: number;
  readonly expiresAt: string | null;
  readonly requiresFulfillmentSelection: boolean;
  readonly requiresAddress: boolean;
  readonly groups: readonly CustomerDeliveryQuoteGroup[];
}

export type CustomerCart = Result<CartService['read']> & {
  readonly currency: string;
  readonly subtotalMinor: number;
  readonly taxMinor: number;
  readonly deliveryFeeMinor: number;
  readonly deliverySurchargeMinor: number;
  readonly deliveryBenefitMinor: number;
  readonly totalMinor: number;
  readonly quote: CustomerDeliveryQuote;
};

export interface CustomerApiClient {
  /**
   * Optional while older hosts migrate. The browser falls back to the first-party
   * client; no screen may depend on analytics transport succeeding.
   */
  readonly analytics?: CustomerAnalyticsClient;
  readonly bootstrap: {
    get(marketId?: string): Promise<CustomerBootstrap>;
  };
  readonly discovery: {
    homepage(marketId?: string): Promise<Result<DiscoveryService['homepage']>>;
    categories(marketId?: string): Promise<Result<DiscoveryService['categories']>>;
    category(slug: string, marketId?: string): Promise<Result<DiscoveryService['category']>>;
  };
  readonly search: {
    search(input: SearchRequest, marketId?: string): Promise<Result<SearchService['search']>>;
    suggestions(term: string, marketId?: string): Promise<Result<SearchService['suggestions']>>;
  };
  readonly products: {
    detail(slug: string, marketId?: string): Promise<Result<ProductService['detail']>>;
    related(slug: string): Promise<Result<ProductService['related']>>;
  };
  readonly stores: {
    nearby(marketId: string): Promise<Result<StoreService['nearby']>>;
    afterHours(marketId: string): Promise<Result<StoreService['nearby']>>;
    detail(slug: string, marketId?: string): Promise<Result<StoreService['detail']>>;
  };
  readonly location: {
    resolve(input: LocationRequest): Promise<Result<LocationService['resolve']>>;
  };
  readonly account: {
    hub(): Promise<Result<AccountService['hub']>>;
    profile(): Promise<Result<AccountService['getProfile']>>;
    consents(): Promise<Awaited<ReturnType<AccountService['listConsents']>>>;
    recordConsent(input: {
      consentType: 'MARKETING_EMAIL' | 'ANALYTICS';
      granted: boolean;
    }): Promise<Awaited<ReturnType<AccountService['recordConsent']>>>;
  };
  readonly favorites: {
    page(): Promise<Result<FavoritesService['page']>>;
    favoriteProduct(productId: string): Promise<void>;
    unfavoriteProduct(productId: string): Promise<void>;
    favoriteStore(input: { locationId: string; retailerId: string }): Promise<void>;
    unfavoriteStore(favoriteId: string): Promise<void>;
  };
  readonly lists: {
    list(): Promise<Result<ListsService['list']>>;
    read(listId: string): Promise<Result<ListsService['read']>>;
    create(name: string): Promise<Result<ListsService['create']>>;
    rename(listId: string, name: string): Promise<void>;
    delete(listId: string): Promise<void>;
    addItem(
      listId: string,
      input: {
        productId: string;
        variantId?: string | null;
        listingId?: string | null;
        quantity: number;
      },
    ): Promise<Result<ListsService['addItem']>>;
    removeItem(listId: string, itemId: string): Promise<void>;
    addAllAvailableToCart(listId: string): Promise<Result<ListsService['addAllAvailableToCart']>>;
  };
  readonly addresses: {
    list(): Promise<Result<AddressService['list']>>;
    create(input: AddressInput): Promise<Result<AddressService['create']>>;
    update(addressId: string, input: AddressInput): Promise<void>;
    delete(addressId: string): Promise<void>;
    setDefault(addressId: string): Promise<void>;
  };
  readonly cart: {
    read(addressId?: string): Promise<CustomerCart>;
    addItem(input: { listingId: string; quantity: number }): Promise<CustomerCart>;
    changeQuantity(itemId: string, quantity: number): Promise<CustomerCart>;
    removeItem(itemId: string): Promise<CustomerCart>;
    changeFulfillmentMode(
      groupId: string,
      fulfillmentMode: 'PICKUP' | 'DELIVERY' | 'OVERNIGHT',
    ): Promise<Result<CartService['changeFulfillmentMode']>>;
  };
  readonly checkout: {
    createPaymentToken(input: { provider: 'MOCK' | 'SANDBOX' }): Promise<{
      readonly paymentTokenReference: string;
      readonly provider: 'MOCK' | 'SANDBOX';
      readonly expiresAt: string;
    }>;
    place(input: {
      cartId: string;
      addressId: string;
      deliveryQuoteId: string;
      paymentTokenReference: string;
      provider: 'MOCK' | 'SANDBOX';
      idempotencyKey: string;
    }): Promise<Awaited<ReturnType<CheckoutService['place']>>>;
    capture(input: {
      orderId: string;
      amountMinor: number;
      idempotencyKey: string;
    }): Promise<Awaited<ReturnType<PaymentService['capture']>>>;
  };
  readonly membership: {
    plans(): Promise<Awaited<ReturnType<MembershipService['listPlans']>>>;
    enroll(input: {
      planId: string;
      provider: 'MOCK' | 'SANDBOX';
      paymentTokenReference: string;
    }): Promise<Awaited<ReturnType<MembershipService['enroll']>>>;
  };
  readonly notifications: {
    get(): Promise<Result<NotificationPreferencesService['get']>>;
    update(
      input: Parameters<NotificationPreferencesService['update']>[1],
    ): Promise<Result<NotificationPreferencesService['update']>>;
  };
  readonly orders: {
    list(): Promise<readonly Result<OrdersService['listCustomer']>[number][]>;
    detail(orderId: string): Promise<Result<OrdersService['customerOrder']>>;
    cancel(orderId: string, reason?: string): Promise<Result<OrdersService['transitionOrder']>>;
    tracking(fulfilmentId: string): Promise<Result<FulfilmentService['customerTracking']>>;
    requestReturn(
      subOrderId: string,
      input: Parameters<ReturnsService['request']>[1]['lines'] extends infer Lines
        ? { lines: Lines }
        : never,
    ): Promise<Result<ReturnsService['request']>>;
    returnDetail(returnRequestId: string): Promise<Result<ReturnsService['customerReturn']>>;
    cancelReturn(
      returnRequestId: string,
      reason?: string,
    ): Promise<Result<ReturnsService['transition']>>;
  };
  readonly leads: {
    submitCustomer(
      input: Parameters<CustomerLeadsService['submit']>[0],
    ): Promise<Result<CustomerLeadsService['submit']>>;
    submitRetailer(
      input: Parameters<RetailerLeadsService['submit']>[0],
    ): Promise<Result<RetailerLeadsService['submit']>>;
    submitMembershipInterest(
      input: Parameters<MembershipInterestService['submit']>[0],
    ): Promise<Result<MembershipInterestService['submit']>>;
  };
  readonly auth: {
    signIn(input: { email: string; password: string }): Promise<AuthOutcome>;
    signUp(input: SignUpInput): Promise<AuthOutcome>;
    requestPasswordReset(email: string): Promise<void>;
    signOut(): Promise<void>;
  };
}

export interface SearchRequest {
  term?: string;
  categorySlug?: string;
  sort?: string;
  filters?: Readonly<Record<string, readonly string[]>>;
  page?: number;
}

export interface LocationRequest {
  postalCode?: string;
  line1?: string;
  city?: string;
  region?: string;
}

export interface AddressInput {
  label: string;
  recipientName: string;
  /** Optional; it is sent only when a customer provides it. */
  phone?: string;
  line1: string;
  line2?: string;
  city: string;
  region: string;
  postalCode: string;
  countryCode?: string;
  deliveryNotes?: string;
}

export interface SignUpInput {
  email: string;
  password: string;
  displayName: string;
  phone?: string;
  postalCode?: string;
  marketingEmailConsent: boolean;
  marketingSmsConsent: boolean;
  signupSource?: string;
  visitorId?: string;
}

export interface CustomerBootstrap {
  readonly actor: {
    readonly signedIn: boolean;
    readonly userId?: string;
    readonly kind?: string;
    readonly permissions?: readonly string[];
  };
  readonly flags: Readonly<Record<string, boolean>>;
  readonly markets: readonly {
    readonly id: string;
    readonly slug: string;
    readonly name: string;
    readonly timezone: string;
  }[];
  readonly selectedMarketId: string | null;
  readonly mediaPublicBaseUrl: string | null;
}

/**
 * Authentication never reports why a credential failed.
 *
 * Distinguishing "no such account" from "wrong password" turns the sign-in form
 * into an account enumeration oracle, so the failure case carries a single
 * generic message.
 */
export type AuthOutcome =
  | { readonly ok: true; readonly customerId: string }
  | { readonly ok: false; readonly message: string };

export const GENERIC_AUTH_FAILURE = 'Email or password is incorrect.';
